import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const CreateClub: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    club_name: '',
    description: '',
    is_visitor_club: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.club_name.trim()) {
      setError('Club name is required');
      return false;
    }

    if (!formData.description.trim()) {
      setError('Description is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);
      
      // Capitalize club name (matching backend behavior)
      const capitalizedClubName = formData.club_name
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      await api.post('/clubs', {
        club_name: capitalizedClubName,
        description: formData.description.trim(),
        is_visitor_club: formData.is_visitor_club,
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/app/dashboard');
      }, 2000);
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Failed to create club. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Create New Club
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Register a new club in the system
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Club created successfully! Redirecting...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Club Name"
            name="club_name"
            value={formData.club_name}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
            helperText="Club name will be automatically capitalized"
          />
          
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
            multiline
            rows={3}
            helperText="Provide a brief description of the club"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_visitor_club}
                onChange={handleChange}
                name="is_visitor_club"
                disabled={loading}
              />
            }
            label="This is a visitor/fly card club"
            sx={{ mt: 2, mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/app/admin/clubs')}
              disabled={loading}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || success}
              fullWidth
              sx={{
                background: 'linear-gradient(45deg, #ff00fb 30%, #ff66fc 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #cc00c8 30%, #ff00fb 90%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Club'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateClub;